<template>
  <div
    class="selection-wheel"
    :class="{running, 'prevent-blur-on-desktop': preventBlurOnDesktop}"
    @mousedown.prevent="onMouseDown"
    @mousemove.prevent="onMouseMove"
    @mouseup.prevent="onMouseUp"
    @touchstart="onMouseDown"
    @touchmove.prevent="onMouseMove"
    @touchend="onMouseUp"
    @mousewheel="onMouseWheel"
    @DOMMouseScroll="onMouseWheel"
    >
    <div class="rotating-wheel" :style="wholeWheelStyle()">
<!--       <div
        class="concept"
        v-for="(concept, i) in concepts"
        :style="conceptLabelStyle(concept, i)"
        @click="onConceptClick(concept)"
        :class="{selected: conceptIsSelected(concept), confirmed: conceptIsConfirmed(concept), disabled: !conceptIsSelected(concept) && selected.length >= maxSelection}"
      >
        <CurvedTextCanvas :text="concept.label.toUpperCase()" :radius="conceptLabelPosition(i).r" />
      </div> -->

      <CurvedText
        v-for="(concept, i) in concepts"
        :key="concept.id"
        class="concept"
        :enabled="concept.label.length > 20"
        :text="concept.label.toUpperCase()"
        :radius="conceptLabelPosition(i).r"
        @click.native="onConceptClick(concept)"
        :externalStyle="conceptLabelStyle(concept, i)"
        :class="{selected: conceptIsSelected(concept), confirmed: conceptIsConfirmed(concept), disabled: !conceptIsSelected(concept) && selected.length >= maxSelection}"
        />
    </div>
    <div class="fader" />
  </div>
</template>

<script>
import find from 'lodash/find'
import shuffle from 'lodash/shuffle'
// import rand from 'random-seed'
import Viewport from '@/mixins/Viewport'
import RAF from '@/utils/RAF'
import ElasticNumber from '@/utils/ElasticNumber'
import CurvedText from '@/atoms/CurvedText'
import CurvedTextCanvas from '@/atoms/CurvedTextCanvas'
import normalizeWheel from '@/utils/normalizeWheel'
import delay from 'lodash/delay'
/* global THREE */

// this allows multiple instances of selection wheel
// to share the same data
// allows the frozen glass effect
const sharedData = {
  radii: [],
  angles: [],
  wheelRotation: new ElasticNumber(0),
  isMouseDown: false,
  isPreparing: true,
  mouseDownT: 0,
  mouseDownWheelRot: 0
}

export default {
  name: 'ConceptsSelectionWheel',

  components: {
    CurvedText,
    CurvedTextCanvas
  },

  mixins: [Viewport],

  props: {
    concepts: {
      type: Array,
      required: true
    },
    selected: {
      type: Array,
      required: false
    },
    positions: {
      type: Array,
      required: true
    },
    maxSelection: {
      type: Number,
      required: false,
      default: 10
    },
    preventBlurOnDesktop: {
      type: Boolean,
      default: false
    }
  },

  data: function () {
    return sharedData
  },

  mounted: function () {
    this._clock = new THREE.Clock()
    this.onConceptsChange(this.concepts)
    this._tempPositionObject = {}
    RAF.add(this.render)
    delay(() => {
      this.isPreparing = false
    }, 500)
  },

  beforeDestroy: function () {
    RAF.remove(this.render)
  },

  methods: {
    conceptOnScreen: function (reference) {
      // let rect = reference.$el.getBoundingClientRect()
      // console.log(rect)
      return true
    },
    render: function () {
      let delta = this._clock.getDelta()
      if (this.running && !this.isMouseDown) {
        this.wheelRotation.target -= 0.0015
      }
      this.wheelRotation.update(delta)

      let positions = []
      for (let i = 0; i < this.concepts.length; i++) {
        let concept = this.concepts[i]
        if (this.conceptIsSelected(concept)) {
          // position is based on index so we need to loop all concepts
          this.conceptLabelPosition(i, this._tempPositionObject, -40, true)
          let concPos = this._internalConceptPositions[i]
          concPos.x = (this._tempPositionObject.x / this.viewPort.width) * 2 - 1
          concPos.y = (this._tempPositionObject.y / this.viewPort.height) * 2 - 1
          concPos.t = this._tempPositionObject.t
          concPos.r = this._tempPositionObject.r
          positions.push(concPos)
        }
      }
      this.$emit('update:positions', positions)
    },
    onConceptClick (concept) {
      let sel = this.selected.concat()
      if (this.conceptIsSelected(concept)) {
        // if concept is already selected then remove it from selection
        concept.confirmed = false
        // push it at the end of the selection
        let index = sel.indexOf(concept)
        if (index !== -1) {
          sel.splice(index, 1)
          sel.push(concept)
        }
      } else {
        // concept is not selected = add it to selection
        sel.push(concept)
      }
      this.$emit('update:selected', sel)
    },

    conceptIsSelected (concept) {
      return find(this.selected, {label: concept.label}) !== undefined
    },
    conceptIsConfirmed (concept) {
      return concept.confirmed
    },
    conceptLabelPosition (index, targetObject = null, radiusOffset = 0, includeWheelRotation = false) {
      let x = this.viewPort.width / 2
      // add (this.viewPort.height / 2) to make circle center below screen
      let y = this.viewPort.height * 2
      let r = (this.radii[index] * y) + radiusOffset

      let wheelRotation = includeWheelRotation ? this.wheelRotation.value + Math.PI / 2 : 0

      let t = this.angles[index] + wheelRotation

      let posX = x + (r * Math.cos(t))
      let posY = y + (r * Math.sin(t))

      if (targetObject) {
        // write to targetObject to save memory
        // if required
        targetObject.x = posX
        targetObject.y = posY
        targetObject.t = t
        targetObject.r = r
        return
      }

      return {
        x: posX,
        y: posY,
        t: t,
        r: r
      }
    },

    wholeWheelStyle () {
      if (!this.isPreparing) {
        let x = this.viewPort.width / 2
        // add (this.viewPort.height / 2) to make circle center below screen
        let y = this.viewPort.height * 2
        return {
          transform: `rotate3d(0, 0, 1, ${this.wheelRotation.value + Math.PI / 2}rad)`,
          transformOrigin: `${x}px ${y}px`
        }
      }
      return {}
    },

    conceptLabelStyle (concept, index) {
      if (!this._tempPositionObject) {
        return {}
      }
      this.conceptLabelPosition(index, this._tempPositionObject)
      let color = this.conceptIsSelected(concept) ? '#' + concept.categoryObject.color.toString(16) : null
      return {
        // transform: `translateX(${this._tempPositionObject.x}px) translateY(${this._tempPositionObject.y}px) rotate(${this._tempPositionObject.t + Math.PI / 2}rad)`,
        transform: `translate3d(${this._tempPositionObject.x}px, ${this._tempPositionObject.y}px, 0) rotate3d(0, 0, 1, ${this._tempPositionObject.t + Math.PI / 2}rad)`,
        color
      }
    },

    generateRandomPlacement () {
      if (this.radii.length > 0 || this.angles.length > 0) {
        return
      }
      this.radii = []
      this.angles = []
      // this.randRadii.seed('dasdasdsa') // initialize random number generator
      // this.randAngles.seed('sax<dxcsda34asef') // initialize random number generator
      // minimum radius (0 - 1)
      const minrad = 0.65
      // maximum radius (0 - 1)
      const maxrad = 0.95
      // number of radii to subdivide our circle into
      // less == more sparse word cloud
      // more == more randomized (but closer)
      const numradii = 6
      // more angles = more close to each other words
      const numAnglesPerRadius = 5

      let radiiAngles = []
      let radStep = (maxrad - minrad) / numradii

      for (let r = 0; r < numradii; r++) {
        let startAngle = Math.random() * (Math.PI * 2)
        let rad = minrad + (r * radStep)
        let nA = Math.round(numAnglesPerRadius + (r * Math.PI)) // more radius = more angles
        for (let t = 0; t < nA; t++) {
          radiiAngles.push({
            angle: startAngle + (t / nA) * (Math.PI * 2),
            radius: rad
          })
        }
      }
      // number of concepts we can accomodate
      console.log('max number of concepts wheel can accomodate', radiiAngles.length)
      console.log('number of concepts', this.concepts.length)
      radiiAngles = shuffle(radiiAngles)

      this.concepts.forEach((concept, index) => {
        this.radii.push(radiiAngles[index].radius)
        this.angles.push(radiiAngles[index].angle)
      })
    },

    onMouseDown: function (event) {
      let x = event.touches && event.touches.length > 0 ? event.touches[0].clientX : event.clientX
      let y = event.touches && event.touches.length > 0 ? event.touches[0].clientY : event.clientY

      x = (this.viewPort.width / 2) - x
      y = (this.viewPort.height) - y
      this.mouseDownT = Math.atan2(x, y)

      this.isMouseDown = true
      this.mouseDownWheelRot = this.wheelRotation.value
    },
    onMouseUp: function (event) {
      this.isMouseDown = false
    },
    onMouseMove: function (event) {
      let x = event.touches && event.touches.length > 0 ? event.touches[0].clientX : event.clientX
      let y = event.touches && event.touches.length > 0 ? event.touches[0].clientY : event.clientY

      if (this.isMouseDown) {
        x = (this.viewPort.width / 2) - x
        y = (this.viewPort.height) - y
        let deltaT = this.mouseDownT - Math.atan2(x, y)
        this.wheelRotation.target = this.mouseDownWheelRot + (deltaT * 2)
      }
    },
    onMouseWheel: function (event) {
      let normalized = normalizeWheel(event)
      event.preventDefault()
      // console.log(normalized.pixelY, normalized.pixelX)
      this.wheelRotation.target -= (normalized.pixelY + normalized.pixelX) * 0.001
    },
    onConceptsChange: function (value) {
      this._internalConceptPositions = value.map(concept => {
        return {
          x: 0,
          y: 0,
          concept
        }
      })
      this.generateRandomPlacement()
    }
  },

  computed: {
    running: function () {
      let running = true
      this.selected.some((concept) => {
        if (!concept.confirmed) {
          running = false
          return true
        }
      })
      return running
    }
  },

  watch: {
    concepts: function (value) {
      // console.log('concepts change')
      this.onConceptsChange(value)
    }
  }

}
</script>

<style scoped lang="scss">
@import 'variables';



@import 'mixins';

.selection-wheel {
  filter: blur(10px);
  pointer-events: none;
  z-index: $z-background;

  &.running {
    filter: none;
    pointer-events: all;
    z-index: $z-foreground;
  }

  @include breakpoint(#{$bp-sm}px) {
    &.prevent-blur-on-desktop {
      filter: none;
    }
  }
}

.concept {
  position: absolute;
  transform-origin: 0% 0%;
  -webkit-user-drag: none;
  user-select: none;
  cursor: pointer;
  transition: opacity 0.2s $ease-in-out-quad, font-size 0.2s $ease-in-out-quad;
  backface-visibility: hidden;
  font-weight: 600;
  font-size: 14px;

  // /deep/ .curved-text-letter {
  //   transition: color 3s $ease-in-out-quad 0.5s;
  // }

  &.selected {
    font-weight: 700;
    font-size: 18px;
  }

  &.confirmed {
    opacity: 0;
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }
}

.rotating-wheel {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  will-change: transform;
}

.fader {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 150px;
  display: none;
  background: linear-gradient(to bottom, color-alpha($c-background, 0), $c-background, $c-background, $c-background);
  pointer-events: none;

  @include breakpoint(#{$bp-sm}px) {
    display: block;
  }
}

</style>



// WEBPACK FOOTER //
// src/molecules/ConceptsSelectionWheel.vue